/**
 * Giriş/Çıkış sayfası — BOSS + Yönetici ortak.
 * Aktif personeli yeşil (mesaide) veya kırmızı (mesai dışı) çerçeve ile gösterir.
 * Real-time: kayitlar koleksiyonu onSnapshot ile dinlenir, her giriş/çıkışta anında güncellenir.
 *
 * Yönetici sadece kendi şubesindeki personeli görür.
 */

import { useEffect, useState, useMemo } from 'react'
import { ArrowLeftRight, Activity, Clock, Search, History } from 'lucide-react'
import {
  collection, query, where, onSnapshot, Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { sonKayitHaritasi, type Kayit } from '@/lib/services/kayit-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { useAuth } from '@/contexts/AuthContext'
import { KayitGecmisiModal } from '@/components/kayit/KayitGecmisiModal'
import clsx from 'clsx'
import type { Vardiyali, Sube, KullaniciPersonel } from '@/types'

export function GirisCikisPage() {
  const { kullanici } = useAuth()
  const isBoss = kullanici?.rol === 'boss'

  const benimSubeIdleri = useMemo<string[] | null>(() => {
    if (isBoss) return null
    if (kullanici?.rol !== 'personel') return []
    return (kullanici as KullaniciPersonel).subeIdleri ?? []
  }, [isBoss, kullanici])

  const [personeller, setPersoneller] = useState<Vardiyali[]>([])
  const [subeler, setSubeler] = useState<Map<string, Sube>>(new Map())
  const [bugunkuKayitlar, setBugunkuKayitlar] = useState<Kayit[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [arama, setArama] = useState('')
  const [filtre, setFiltre] = useState<'hepsi' | 'mesaide' | 'mesaiDisi'>('hepsi')

  // Kayıt geçmişi modal (sadece BOSS) — kart tıklanınca o personelle açılır
  const [gecmisAcik, setGecmisAcik] = useState(false)
  const [gecmisPersonelId, setGecmisPersonelId] = useState<string | undefined>(undefined)

  function kayitlariAc(personelId?: string) {
    setGecmisPersonelId(personelId)
    setGecmisAcik(true)
  }

  useEffect(() => {
    let iptal = false
    async function ilkYukle() {
      try {
        setYukleniyor(true)
        const [p, s] = await Promise.all([
          tumPersonelleriGetir(),
          tumSubeleriGetir(),
        ])
        if (iptal) return
        const aktif = p.filter(x => x.aktif)
        const filtreliPersoneller = benimSubeIdleri === null
          ? aktif
          : aktif.filter(x => x.subeIdleri?.some(id => benimSubeIdleri.includes(id)) ?? false)
        const subeMap = new Map<string, Sube>()
        s.forEach(x => {
          if (benimSubeIdleri === null || benimSubeIdleri.includes(x.id)) {
            subeMap.set(x.id, x)
          }
        })
        setPersoneller(filtreliPersoneller)
        setSubeler(subeMap)
      } catch (err) {
        if (!iptal) {
          console.error(err)
          setHata('Personel yüklenemedi.')
        }
      }
    }
    ilkYukle()
    return () => { iptal = true }
  }, [benimSubeIdleri])

  useEffect(() => {
    const db = getFirestoreDb()
    const baslangic = new Date()
    baslangic.setHours(0, 0, 0, 0)
    const ts = Timestamp.fromDate(baslangic)

    const q = query(collection(db, 'kayitlar'), where('zaman', '>=', ts))
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
        liste.sort((a, b) => b.zaman.toMillis() - a.zaman.toMillis())
        const filtreli = benimSubeIdleri === null
          ? liste
          : liste.filter(k => benimSubeIdleri.includes(k.subeId))
        setBugunkuKayitlar(filtreli)
        setYukleniyor(false)
      },
      (err) => {
        console.error(err)
        setHata('Kayıt akışı dinlenemedi.')
        setYukleniyor(false)
      }
    )
    return () => unsubscribe()
  }, [benimSubeIdleri])

  const sonKayitlar = useMemo(() => sonKayitHaritasi(bugunkuKayitlar), [bugunkuKayitlar])

  const filtreliListe = useMemo(() => {
    const q = arama.trim().toLowerCase()
    return personeller.filter(p => {
      if (q && !p.isim.toLowerCase().includes(q) && !(p.telefon ?? '').includes(q)) return false
      const son = sonKayitlar.get(p.id)
      const mesaide = son?.tip === 'giris'
      if (filtre === 'mesaide' && !mesaide) return false
      if (filtre === 'mesaiDisi' && mesaide) return false
      return true
    })
  }, [personeller, arama, filtre, sonKayitlar])

  const sayilar = useMemo(() => {
    let mesaide = 0
    personeller.forEach(p => {
      if (sonKayitlar.get(p.id)?.tip === 'giris') mesaide++
    })
    return {
      mesaide,
      mesaiDisi: personeller.length - mesaide,
      toplam: personeller.length,
    }
  }, [personeller, sonKayitlar])

  function saatGoster(ts: Timestamp): string {
    return ts.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="w-6 h-6 text-brand-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Giriş / Çıkış</h1>
          <p className="text-sm text-gray-600 mt-0.5 inline-flex items-center gap-1">
            <Activity className="w-3 h-3 text-green-600 animate-pulse" />
            Canlı — her giriş/çıkışta anında güncellenir
          </p>
        </div>
        {isBoss && (
          <button
            onClick={() => kayitlariAc(undefined)}
            className="btn-secondary inline-flex items-center gap-2 flex-shrink-0"
          >
            <History className="w-4 h-4" />
            Kayıt Geçmişi
          </button>
        )}
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
          {hata}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => setFiltre('hepsi')}
          className={clsx(
            'card text-center py-3 transition-colors',
            filtre === 'hepsi' ? 'ring-2 ring-brand-500' : ''
          )}
        >
          <div className="text-2xl font-bold text-gray-900">{sayilar.toplam}</div>
          <div className="text-xs text-gray-600 uppercase">Toplam</div>
        </button>
        <button
          onClick={() => setFiltre('mesaide')}
          className={clsx(
            'card text-center py-3 transition-colors',
            filtre === 'mesaide' ? 'ring-2 ring-green-500 bg-green-50/40' : ''
          )}
        >
          <div className="text-2xl font-bold text-green-700">{sayilar.mesaide}</div>
          <div className="text-xs text-gray-600 uppercase">Mesaide</div>
        </button>
        <button
          onClick={() => setFiltre('mesaiDisi')}
          className={clsx(
            'card text-center py-3 transition-colors',
            filtre === 'mesaiDisi' ? 'ring-2 ring-red-500 bg-red-50/40' : ''
          )}
        >
          <div className="text-2xl font-bold text-red-700">{sayilar.mesaiDisi}</div>
          <div className="text-xs text-gray-600 uppercase">Mesai Dışı</div>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          placeholder="İsim veya telefon ara..."
          className="input-base pl-9"
        />
      </div>

      {yukleniyor && personeller.length === 0 ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filtreliListe.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          {personeller.length === 0
            ? (isBoss ? 'Personel yok.' : 'Şubende personel yok.')
            : 'Arama sonucu bulunamadı.'}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {filtreliListe.map(p => {
            const son = sonKayitlar.get(p.id)
            const mesaide = son?.tip === 'giris'
            const subeAdi = subeler.get(son?.subeId ?? '')?.isim
              ?? p.subeIdleri.map(id => subeler.get(id)?.isim).filter(Boolean).join(' · ')
              ?? '—'
            const url = (p as Vardiyali & { profilFotoUrl?: string }).profilFotoUrl

            return (
              <div
                key={p.id}
                onClick={isBoss ? () => kayitlariAc(p.id) : undefined}
                className={clsx(
                  'card transition-all',
                  isBoss && 'cursor-pointer hover:shadow-md',
                  mesaide
                    ? 'ring-2 ring-green-400 bg-green-50/40'
                    : 'ring-2 ring-red-400 bg-red-50/40'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-gray-900 truncate">{p.isim}</p>
                      {mesaide ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full inline-flex items-center gap-0.5 font-bold uppercase">
                          <Activity className="w-2.5 h-2.5 animate-pulse" />
                          Mesaide
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-bold uppercase">
                          Mesai Dışı
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 truncate">{subeAdi}</p>
                    {son ? (
                      <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {son.tip === 'giris' ? 'Giriş' : 'Çıkış'}: {saatGoster(son.zaman)}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1 italic">Bugün kayıt yok</p>
                    )}
                  </div>
                  {url ? (
                    <img
                      src={url}
                      alt={p.isim}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0 self-center"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 self-center">
                      {p.isim.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {isBoss && (
        <KayitGecmisiModal
          acik={gecmisAcik}
          onKapat={() => setGecmisAcik(false)}
          acilPersonelId={gecmisPersonelId}
        />
      )}
    </div>
  )
}
